import { useEffect, useRef } from 'react';

/**
 * Hook to lock body scroll when a modal/overlay is open
 * Handles iOS Safari quirks and prevents background scroll on mobile
 * 
 * @param isLocked - Whether scroll should be locked
 */
export function useScrollLock(isLocked: boolean) {
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    if (!isLocked) return;

    // Save current scroll position
    scrollPositionRef.current = window.scrollY;

    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Lock body scroll with iOS Safari fix (position: fixed)
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPositionRef.current}px`;
    document.body.style.width = '100%';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // Prevent touch scrolling on mobile
    const preventScroll = (e: TouchEvent) => {
      // Allow scrolling inside modal elements with data-scroll-lock-ignore
      const target = e.target as HTMLElement;
      if (target.closest('[data-scroll-lock-ignore]')) {
        return;
      }
      e.preventDefault();
    };

    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      // Restore scroll position and styles
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      
      // Restore scroll position
      window.scrollTo(0, scrollPositionRef.current);
      
      // Remove touch listener
      document.removeEventListener('touchmove', preventScroll);
    };
  }, [isLocked]);
}
